import {
  WithAnalyticsEventsProps,
  withAnalyticsEvents
} from '@atlaskit/analytics-next';
import { Button, ButtonProps } from '@atlassiansox/wpl-utils';
import React, { MouseEvent, ReactNode, useCallback } from 'react';
import { controlsContainerStyle } from './progressive-flow.styles';

export interface ProgressiveFlowProps {
  readonly screenIndex: number;

  readonly onPreviousButtonClick?: () => void;
  readonly showPreviousButton?: boolean;
  readonly enablePreviousButton?: boolean;
  readonly previousButtonLabel?: string;

  readonly onNextButtonClick: () => void;
  readonly enableNextButton: boolean;
  readonly showNextButton: boolean;
  readonly nextButtonLabel: string;

  readonly maxWidth?: string;
}

const NavigationButtonBase = ({
  createAnalyticsEvent,
  onClick,
  ...rest
}: WithAnalyticsEventsProps & ButtonProps) => {
  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const analyticsEvent = createAnalyticsEvent?.({
        action: 'click',
        component: 'button',
        eventName: 'progressive_flow_navigation'
      });

      analyticsEvent?.fire();

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, createAnalyticsEvent]
  );

  return <Button {...rest} type="primary" onClick={handleClick} />;
};

const PreviousButton = withAnalyticsEvents()(NavigationButtonBase);
const NextButton = withAnalyticsEvents()(NavigationButtonBase);

const ProgressiveFlow = ({
  screenIndex = 0,

  onPreviousButtonClick,
  showPreviousButton,
  enablePreviousButton,
  previousButtonLabel,

  onNextButtonClick,
  enableNextButton,
  showNextButton,
  nextButtonLabel,

  maxWidth,

  children
}: React.PropsWithChildren<ProgressiveFlowProps>) => {
  const childrenIsIterable = (
    passedChildren?: typeof children
  ): passedChildren is Iterable<ReactNode> =>
    typeof (passedChildren as Iterable<ReactNode>)[Symbol.iterator] ===
    'function';

  if (!childrenIsIterable(children)) {
    throw new Error(
      'Progressive Flow component requires at least two child components.'
    );
  }

  return (
    <div
      data-testid="progressive-flow"
      style={{ maxWidth: maxWidth || 'auto' }}
    >
      {Array.from(children)[screenIndex]}

      <div css={controlsContainerStyle}>
        {showPreviousButton && (
          <PreviousButton
            disabled={!enablePreviousButton}
            onClick={onPreviousButtonClick}
            text={previousButtonLabel || ''}
          />
        )}
        {showNextButton && (
          <NextButton
            className="next-button"
            text={nextButtonLabel}
            disabled={!enableNextButton}
            onClick={onNextButtonClick}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressiveFlow;
